import client from "@/plugins/contentful"

import {
  STORE_ENTRY,
  STORE_ENTRIES,
  STORE_PREVIEW_ENTRY,
  STORE_PREVIEW_ENTRIES,
} from "@/store/mutation-types"
import { unionState } from "@common/store/helpers"

const state = () => ({
  previews: [],
  entries: [],
})

const actions = {
  async fetchEntryById({ commit }, { entryId }) {
    return client.getEntry(entryId).then((entry) => {
      commit(STORE_ENTRY, [entry.toPlainObject()])
      return entry
    })
  },

  async fetchEntryBySlug({ commit }, { type, slug }) {
    return client
      .getEntries({
        content_type: type,
        "fields.slug[in]": slug,
        include: 5,
      })
      .then((entries) => {
        commit(STORE_ENTRY, [entries.items[0]])
        return entries.items[0]
      })
  },
  async fetchAllEntriesByTagInclusion(
    { commit },
    { type, tags, limit = undefined, order = "fields.order" },
  ) {
    let entries = await client.getEntries({
      content_type: type,
      "metadata.tags.sys.id[in]": String(tags),
      order,
    })
    entries = entries.toPlainObject()
    entries = entries.items.map((o) => {
      return { ...o, id: o.sys.id }
    })
    commit(STORE_ENTRIES, entries)
    return entries
  },

  async fetchAllPreviewsByTagInclusion(
    { commit },
    {
      type,
      tags,
      order = "fields.order",
      limit = undefined,
      select = "sys.id,sys.contentType,fields.title, fields.subtitle,metadata.tags,fields.description,fields.slug,fields.thumbnailImage,fields.heroImage,fields.order,fields.author,fields.meta, fields.createdAt",
    },
  ) {
    var entries = await client.getEntries({
      content_type: type,
      "metadata.tags.sys.id[in]": String(tags),
      select,
      order,
      include: 5,
      limit,
    })
    entries = entries.toPlainObject()
    entries = entries.items.map((o) => {
      return { ...o, id: o.sys.id }
    })
    commit(STORE_PREVIEW_ENTRIES, entries)
    return entries
  },


  async fetchActiveFeatureReleases(
    { commit },
    { contentType = "productFeatureRelease", tags, limit = 50 } = {},
  ) {
    const getField = (obj, key) => {
      const v = obj?.fields?.[key]
      return typeof v === "object" && v !== null && !Array.isArray(v) && !v?.sys
        ? Object.values(v)[0]
        : v
    }
    try {
      const query = {
        content_type: contentType,
        order: "-sys.createdAt",
        limit,
        include: 5,
      }
      if (tags) {
        query["metadata.tags.sys.id[in]"] = String(tags)
      }
      const response = await client.getEntries(query)
      const now = new Date()
      const isProd = import.meta.env.VITE_SENTRY_ENVIRONMENT === "production"
      const items = (response.items || [])
        .filter((entry) => {
          const startDate = getField(entry, "startDate")
          const endDate = getField(entry, "endDate")
          const prodReady = getField(entry, "prodReady")
          if (startDate && new Date(startDate) > now) return false
          if (endDate && new Date(endDate) < now) return false
          if (isProd && !prodReady) return false
          return true
        })
        .map((o) => ({ ...o, id: o.sys?.id }))
      commit(STORE_ENTRIES, items)
      return items
    } catch (err) {
      console.error("[content] fetchActiveFeatureReleases failed:", err)
      return []
    }
  },

  fetchByDepartmentCode({ commit }, { departmentCode }) {
    return axios
      .get(`/departments/by-code/${departmentCode}`)
      .then((response) => {
        return response.data
      })
  },
}

const mutations = {
  [STORE_ENTRY](state, entries) {
    state.entries = unionState(state.entries, entries)
  },
  [STORE_ENTRIES](state, entries) {
    state.entries = unionState(state.entries, entries)
  },
  [STORE_PREVIEW_ENTRY](state, entries) {
    state.entries = unionState(state.entries, entries)
  },
  [STORE_PREVIEW_ENTRIES](state, previews) {
    state.previews = unionState(state.previews, previews)
  },
}

const getters = {
  getEntryById: (state) => (entryId) =>
    state.entries.find((e) => e.sys.id === entryId),
  getEntryBySlug: (state) => (slug) =>
    state.entries.find((e) => e.fields.slug === slug),
  getEntriesByTagInclusion: (state) => (tags) =>
    state.entries.filter((e) => {
      const entryTags = e.metadata.tags.map((o) => o.sys.id)
      return entryTags.some((val) => tags.includes(val))
    }),
  getPreviewById: (state) => (entryId) =>
    state.entries.find((e) => e.sys.id === entryId),
  getPreviewsByTagSet: (state) => (tags) =>
    state.previews.filter((e) => {
      const entryTags = e.metadata.tags.map((o) => o.sys.id)
      return tags.every((val) => entryTags.includes(val))
    }),
  getPreviewsByTagInclusion: (state) => (tags) =>
    state.previews.filter((e) => {
      const entryTags = e.metadata.tags.map((o) => o.sys.id)
      return entryTags.some((val) => tags.includes(val))
    }),

  getFeatureReleases: (state) => {
    const getField = (obj, key) => {
      const v = obj?.fields?.[key]
      return typeof v === "object" && v !== null && !Array.isArray(v) && !v?.sys
        ? Object.values(v)[0]
        : v
    }
    return (state.entries || [])
      .filter(
        (e) =>
          e.sys?.contentType?.sys?.id === "productFeatureRelease" ||
          e.sys?.contentType === "productFeatureRelease",
      )
      .map((entry) => {
        const title = getField(entry, "title") || ""
        const description = getField(entry, "description") || ""
        const startDate = getField(entry, "startDate")
        const videoUrl = getField(entry, "videoYoutubeUrl")
        const thumb = getField(entry, "thumbnailImage")
        const mediaUrl =
          videoUrl || thumb?.fields?.file?.url || thumb?.file?.url || null
        const mediaType = videoUrl ? "youtube" : "image"
        const blocks = entry?.fields?.blocks || []
        return {
          id: entry?.sys?.id,
          title,
          description,
          mediaUrl,
          mediaType,
          startDate: startDate || entry?.sys?.createdAt,
          blocks,
        }
      })
  },

  getPreviewsByTagInclusionAndCreatedAt: (state) => (tags) =>
    state.previews
      .filter((e) => {
        const entryTags = e.metadata.tags.map((o) => o.sys.id)
        return entryTags.some((val) => tags.includes(val))
      })
      .sort(function (a, b) {
        // Turn your strings into dates, and then subtract them
        // to get a value that is either negative, positive, or zero.
        return (
          new Date(b["fields"]["createdAt"]) -
          new Date(a["fields"]["createdAt"])
        )
      }),
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
